import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { saveUser } from '../lib/auth';
import { setActiveTenant, clearActiveTenant } from '../lib/api';
import { queryClient } from '../lib/queryClient';
import { clearStoredPreference } from './viewMode.utils';
import * as authService from '../services/authService';
import { authService as legacyAuthService } from '../services/authService';
import { acknowledgeLegal as acknowledgeLegalApi } from '../api/me';
import type { AuthUser, AuthMembership, MembershipOption } from '../types/identity';
import type { GoogleSignupData, SignupData } from '../types/auth';

interface AuthState {
  user: AuthUser | null;
  activeMembership: AuthMembership | null;
  /** All of the user's memberships — drives the workspace switcher. */
  memberships: MembershipOption[];
  /** Set when a login needs an explicit workspace selection (≥2 memberships). */
  pendingMemberships: MembershipOption[] | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  /** True when the active membership still owes an acknowledgement of the
   *  current Terms & Privacy versions (invited members never see the signup
   *  questionnaire). Sourced from /auth/me `legal_ack_required`. */
  legalAckRequired: boolean;
}

export interface AuthContextType extends AuthState {
  login: (creds: authService.LoginCredentials) => Promise<{ requiresSelection: boolean }>;
  signup: (data: SignupData) => Promise<{ requiresSelection: boolean }>;
  signupWithGoogle: (data: GoogleSignupData) => Promise<{ requiresSelection: boolean }>;
  loginWithGoogle: (idToken: string) => Promise<{ requiresSelection: boolean }>;
  selectTenant: (tenantId: string) => Promise<void>;
  switchTenant: (tenantId: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Patch the cached user (e.g. after a profile edit) so the header/avatar
   *  reflect the change without a full re-hydrate. */
  updateUser: (user: AuthUser) => void;
  /** Reflect a branding save in the live session, without a re-hydrate. */
  updateTenantStyling: (styling: Record<string, unknown>) => void;
  /** Record the acknowledgement server-side, then clear `legalAckRequired`. */
  acknowledgeLegal: () => Promise<void>;
}

const initial: AuthState = {
  user: null,
  activeMembership: null,
  memberships: [],
  pendingMemberships: null,
  accessToken: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  legalAckRequired: false,
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/** Build an `AuthMembership` from a login/select/switch result, or null when
 *  the result still requires a workspace selection (no active tenant yet). */
function membershipFrom(r: authService.LoginResult): AuthMembership | null {
  if (!r.tenant || !r.role) return null;
  return {
    tenant: r.tenant,
    role: r.role,
    perms: (r.perms ?? []) as AuthMembership['perms'],
  };
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthState>(initial);

  // Hydrate from the session cookie on mount. The axios interceptor handles
  // 401 → refresh → retry; if getMe still rejects, treat the user as logged out.
  useEffect(() => {
    (async () => {
      try {
        const me = await authService.getMe();
        const active = membershipFrom(me);
        saveUser(me.user);
        if (active) {
          // Keep the axios tenant header in sync with the hydrated session so
          // X-Tenant-ID is present on the first request after a page refresh.
          setActiveTenant(active.tenant.id);
        }
        setState({
          ...initial,
          isLoading: false,
          isAuthenticated: !!active,
          user: me.user,
          activeMembership: active,
          memberships: me.memberships ?? [],
          // /auth/me echoes the request's own access token so the chat socket
          // can re-authenticate after a refresh (where login state is restored
          // from cookies, not an in-body token).
          accessToken: me.access_token ?? null,
          // Absent while the backend rolls out — an unknown flag means "nothing owed".
          legalAckRequired: me.legal_ack_required ?? false,
        });
      } catch {
        localStorage.removeItem('tereiko-user');
        setState({ ...initial, isLoading: false });
      }
    })();
  }, []);

  const applyResult = useCallback(
    (r: authService.LoginResult): { requiresSelection: boolean } => {
      if (r.requires_tenant_selection) {
        setState((p) => ({
          ...p,
          user: r.user,
          activeMembership: null,
          pendingMemberships: r.memberships ?? [],
          isAuthenticated: false,
          isLoading: false,
          error: null,
        }));
        return { requiresSelection: true };
      }

      const active = membershipFrom(r);
      saveUser(r.user);
      if (active) {
        // Keep the axios tenant header in sync with the active workspace.
        setActiveTenant(active.tenant.id);
      }
      setState((p) => ({
        ...p,
        user: r.user,
        activeMembership: active,
        memberships: r.memberships ?? p.memberships,
        pendingMemberships: null,
        accessToken: r.access_token ?? null,
        isAuthenticated: !!active,
        isLoading: false,
        error: null,
        legalAckRequired: r.legal_ack_required ?? false,
      }));
      return { requiresSelection: false };
    },
    []
  );

  const login = useCallback(
    async (creds: authService.LoginCredentials) => {
      setState((p) => ({ ...p, error: null }));
      try {
        return applyResult(await authService.login(creds));
      } catch (e: any) {
        const message = e?.response?.data?.error || e?.message || 'Login failed';
        setState((p) => ({ ...p, error: message }));
        throw e;
      }
    },
    [applyResult]
  );

  const signup = useCallback(
    async (data: SignupData) => {
      setState((p) => ({ ...p, error: null }));
      try {
        // Create the account (sets session cookies). The signup response omits
        // the permission bundle, which the view-mode + landing logic needs, so
        // hydrate the full session (perms, memberships, token) from /auth/me
        // before resolving — otherwise the fresh Owner would land in Learning.
        await legacyAuthService.signup(data);
        return applyResult(await authService.getMe());
      } catch (e: any) {
        const message = e?.response?.data?.error || e?.message || 'Signup failed';
        setState((p) => ({ ...p, error: message }));
        throw e;
      }
    },
    [applyResult]
  );

  const signupWithGoogle = useCallback(
    async (data: GoogleSignupData) => {
      setState((p) => ({ ...p, error: null }));
      try {
        // Same shape as `signup`: /auth/google/signup sets the session cookies
        // but omits the permission bundle, so hydrate the full session from
        // /auth/me before resolving.
        await legacyAuthService.googleSignup(data);
        return applyResult(await authService.getMe());
      } catch (e: any) {
        const message = e?.response?.data?.error || e?.message || 'Signup failed';
        setState((p) => ({ ...p, error: message }));
        throw e;
      }
    },
    [applyResult]
  );

  const loginWithGoogle = useCallback(
    async (idToken: string) => {
      setState((p) => ({ ...p, error: null }));
      try {
        // googleLogin sets the session cookies but returns no permission bundle;
        // hydrate the full session from /auth/me so AuthContext state (and the
        // view-mode/landing logic) reflects the signed-in member before routing.
        await legacyAuthService.googleLogin({ id_token: idToken });
        return applyResult(await authService.getMe());
      } catch (e: any) {
        const message = e?.response?.data?.error || e?.message || 'Google sign-in failed';
        setState((p) => ({ ...p, error: message }));
        throw e;
      }
    },
    [applyResult]
  );

  const updateUser = useCallback((user: AuthUser) => {
    saveUser(user);
    setState((p) => ({ ...p, user }));
  }, []);

  // A branding save changes the tenant's styling on the server; without this
  // the session kept the old value until the next hydrate, so a Reset cleared
  // the colours but left the old logo on screen — and the branding form, which
  // seeds itself from the session, put it straight back (Aleix, 2026-09-11).
  const updateTenantStyling = useCallback(
    (styling: Record<string, unknown>) => {
      setState((p) =>
        p.activeMembership
          ? {
              ...p,
              activeMembership: {
                ...p.activeMembership,
                tenant: { ...p.activeMembership.tenant, styling },
              },
            }
          : p
      );
    },
    []
  );

  const acknowledgeLegal = useCallback(async () => {
    await acknowledgeLegalApi();
    setState((p) => ({ ...p, legalAckRequired: false }));
  }, []);

  const selectTenant = useCallback(
    async (tenantId: string) => {
      applyResult(await authService.selectTenant(tenantId));
      // Query keys are not tenant-scoped, so drop the previous workspace's
      // cached data to avoid rendering it under the newly selected tenant.
      queryClient.clear();
    },
    [applyResult]
  );

  const switchTenant = useCallback(
    async (tenantId: string) => {
      applyResult(await authService.switchTenant(tenantId));
      // Discard the prior workspace's cached data on a mid-session switch.
      queryClient.clear();
    },
    [applyResult]
  );

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } finally {
      localStorage.removeItem('tereiko-user');
      clearActiveTenant();
      // Reset the view-mode preference so the next member on this browser starts
      // from the default (staff → Manage) rather than inheriting our choice.
      clearStoredPreference();
      // Drop all cached server state so a subsequent login (possibly as a
      // different user/tenant) never renders the prior session's data.
      queryClient.clear();
      setState({ ...initial, isLoading: false });
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        signup,
        signupWithGoogle,
        loginWithGoogle,
        selectTenant,
        switchTenant,
        logout,
        updateUser,
        updateTenantStyling,
        acknowledgeLegal,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
};

export default AuthContext;
